import React, { useCallback, useEffect, useRef, useState } from "react";
import { Layout, Button, Select } from "antd";
import {
  AimOutlined,
  GroupOutlined,
  ExpandOutlined,
  SaveOutlined
} from "@ant-design/icons";
import { HubConnectionBuilder, HttpTransportType } from "@microsoft/signalr";
import { useDispatch } from "react-redux";

import "../assets/libs/fontawesome-5.2.0/css/all.css";
import "../assets/styles/index.scss";

import { Canvas } from "../components/canvas";
import service from "../service";
import {
  setSpinning,
  setSelectedSeats,
  setShowZoomWatermark
} from "../redux/designerPropertiesSlice";
import { useCanvasZoom } from "../hooks/useCanvasZoom";
import { useSeatsVisibility } from "../hooks/useSeatsVisibility";
import { applyAutoFit, useAutoFitOnContainerResize } from "../hooks/useAutoFitOnContainerResize";
import { useSeatHubConnection } from "../hooks/useSeatHubConnection";
import { getSvgObject } from "../utils/ObjectUtil";
import { Icons, ObjectSuperTypesEnum, SelectionTypeEnum } from "../enums";

const MapBlocker = () => {
  const dispatch = useDispatch();
  const canvasRef = useRef();
  const mapContainerRef = useRef();
  const [connection, setConnection] = useState(null);
  const { canZoomIn, canZoomOut } = useCanvasZoom(canvasRef);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [selectionType, setSelectionType] = useState('individual');

  useEffect(() => {
    dispatch(setSpinning(true));
    initScript();
    return () => {
      dispatch(setShowZoomWatermark(false));
    };
  }, []);

  const handleHubChangeStatus = useCallback((value) => {
    canvasRef.current.handler.blockerHandler.setType("seat");
    canvasRef.current.handler.blockerHandler.handleObjectLockUpdate(value);
  }, []);

  useSeatHubConnection(connection, handleHubChangeStatus);

  useSeatsVisibility(canvasRef, canZoomIn, isMapLoaded, (seat) => {
    if (seat.superType === ObjectSuperTypesEnum.Table) {
      if (seat.selectionType !== SelectionTypeEnum.byTable || (seat.status === "Free" && !seat.isSelected)) {
        seat.item(1)?.set({ visible: true });
      }
      return;
    }
    if (seat.status !== "Free" || seat.isSelected) return;// Si el asiento no está libre o está seleccionado, no hace nada

    // Mostrar/ocultar iconos y labels
    const seatCircle = seat.item(0);
    seatCircle.set({ fill: `${seat.statusColor.Free}40`, stroke: seat.statusColor.Free });

    // si el asiento es accesible, de compañia o tiene vista restringida, mostrar icono
    if (seat.accesible || seat.companion || seat.restrictedView) {
      if (seat.restrictedView)
        seat.insertAt(getSvgObject(Icons.restricted, (seatCircle.radius * 1.7), '#000000'), 2, true);
      else if (seat.accesible)
        seat.insertAt(getSvgObject(Icons.accesible, (seatCircle.radius * 1.7), '#000000'), 2, true);
      else if (seat.companion)
        seat.insertAt(getSvgObject(Icons.companion, (seatCircle.radius * 1.7), '#000000'), 2, true);
    } else {
      const seatText = seat.item(1);
      seatText.set({
        visible: true
      });
    }
  }, (seat) => {
    if (seat.superType === ObjectSuperTypesEnum.Table) {
      seat.item(1)?.set({ visible: false });
      return;
    }
    if (seat.status !== "Free" || seat.isSelected) return;// Si el asiento no está libre o está seleccionado, no hace nada

    // Restaurar propiedades customizadas
    const seatCircle = seat.item(0);
    seatCircle.set({ fill: `${seat.statusColor.Free}80`, stroke: "transparent" });

    const seatText = seat.item(1);
    seatText.set({ visible: false });

    const seatIcon = seat.item(2);
    if (seatIcon) {
      seat.remove(seatIcon);
    }
  });

  useAutoFitOnContainerResize(mapContainerRef, canvasRef, isMapLoaded, {
    alignTop: true,
  });

  const initScript = async () => {
    window.redseats.setSettigs(process.env.REACT_APP_ENVIROMENT, "Blocker");
    if (window.redseats.publicKey) await init();
    else window.redseats.component.callback = init;
  };

  const init = async () => {
    const mapEvent = await service.mapEvent.buildNotForSale();
    if (mapEvent.key) {
      dispatch(setSelectedSeats(mapEvent.selectedSeats));
      const mapCanvas = JSON.parse(mapEvent.mapCanvas);
      await handlers.onLoad(mapCanvas);

      //signalr
      const newConnection = new HubConnectionBuilder()
        .withUrl(`${process.env.REACT_APP_HUB_URL}object`, {
          transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
          withCredentials: true,
        })
        .withAutomaticReconnect()
        .build();

      setConnection(newConnection);
    }
  };

  const handlers = {
    onLoad: async ({ Objects }) => {
      if (Objects) {
        canvasRef.current.handler.clear(true);
        const data = Objects.filter((obj) => {
          return obj.id;
        });
        await canvasRef.current.handler.importJSON(data, "blocker");
      }

      canvasRef.current.handler.interactionMode = "blocker";
      canvasRef.current.handler.enablePanning = true;
      dispatch(setSpinning(false));
      setIsMapLoaded(true);
    },
  };

  const handleSaveChanges = () => {
    canvasRef.current.handler.blockerHandler.handleSaveChanges();
  }

  const handleChangeSelect = (value) => {
    canvasRef.current.handler.enablePanning = value == "individual";
    canvasRef.current.handler.isMultipleSelectionEnabled = value == "group";
    canvasRef.current.handler.canvas.skipTargetFind = value == "group";
    setSelectionType(value)
  }

  const handleZoomToFit = () => {
    applyAutoFit(canvasRef);
  }

  return (
    <div className="map">
      <article className="map-canvas-container" ref={mapContainerRef}>
        <div className="map-header-toolbar">
          <Button
            onClick={() => {
              canvasRef.current.handler.zoomHandler.zoomOut();
            }}
            disabled={!canZoomOut}
          >
            <span className="icon-zoom-out"></span>
          </Button>

          <Button
            onClick={handleZoomToFit}
          >
            <ExpandOutlined />
          </Button>
          <Button
            onClick={() => {
              canvasRef.current.handler.zoomHandler.zoomIn();
            }}
            disabled={!canZoomIn}
          >
            <span className="icon-icon-zoom-in"></span>
          </Button>
          <Select
            style={{ width: 140 }}
            onSelect={handleChangeSelect}
            defaultValue="individual"
          >
            <Select.Option value="individual"><AimOutlined /> Individual</Select.Option>
            <Select.Option value="group"><GroupOutlined /> Grupo</Select.Option>
          </Select>
          <Button
            onClick={() => {
              handleSaveChanges()
            }}
          >
            <SaveOutlined /> <span className="label">Guardar</span>
          </Button>
        </div>
        <div
          className="canvas-wrapper"
          onMouseEnter={() => dispatch(setShowZoomWatermark(true))}
          onMouseLeave={() => dispatch(setShowZoomWatermark(false))}
        >
          <Canvas
            ref={canvasRef}
            editable={false}
            canvasOption={{ backgroundColor: "#FFFFFF", defaultCursor: "default" }}
            maxZoom={1000}
            minZoom={100}
            onZoom={(zoom) => dispatch(setShowZoomWatermark(zoom <= 1))}
            gridOption={{ enabled: false }}
          />
        </div>
      </article>
    </div>
  );
};

export default MapBlocker;
